import apiClient, { shouldUseAPI, handleAPIError } from './apiClient';

const mapBackendContact = (contact) => ({
  id: contact.id,
  first_name: contact.first_name || '',
  last_name: contact.last_name || '',
  cuil: contact.cuil || '',
  email: contact.email || '',
  phone: contact.phone || '',
  phone2: contact.phone2 || '',
  address: contact.address || ''
});

const contactsService = {
  getMockContacts() {
    return [
      {
        id: 1,
        first_name: 'Juan',
        last_name: 'Perez',
        cuil: '20123456789',
        email: 'juan.perez@email.com',
        phone: '011-9876-5432',
        phone2: '',
        address: 'Av. Siempre Viva 742'
      },
      {
        id: 2,
        first_name: 'ACME S.A.',
        last_name: '',
        cuil: '30123456789',
        email: 'contacto@acme.com',
        phone: '011-1234-5678',
        phone2: '011-5555-1111',
        address: 'Parque Industrial 1200'
      }
    ];
  },

  getCreatedContacts() {
    try {
      const stored = sessionStorage.getItem('demo_contacts');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error al obtener contactos creados:', error);
      return [];
    }
  },

  saveCreatedContacts(contacts) {
    try {
      sessionStorage.setItem('demo_contacts', JSON.stringify(contacts));
    } catch (error) {
      console.error('Error al guardar contactos creados:', error);
    }
  },

  async searchContacts(query) {
    if (!shouldUseAPI()) {
      const mockContacts = this.getMockContacts();
      const createdContacts = this.getCreatedContacts();
      const allContacts = [...mockContacts, ...createdContacts];

      if (!query || query.trim() === '') {
        return allContacts;
      }

      const searchTerm = query.toLowerCase().trim();
      return allContacts.filter((contact) =>
        (contact.first_name || '').toLowerCase().includes(searchTerm) ||
        (contact.last_name || '').toLowerCase().includes(searchTerm) ||
        (contact.cuil || '').includes(searchTerm) ||
        (contact.email || '').toLowerCase().includes(searchTerm) ||
        (contact.phone || '').toLowerCase().includes(searchTerm) ||
        (contact.phone2 || '').toLowerCase().includes(searchTerm) ||
        (contact.address || '').toLowerCase().includes(searchTerm)
      );
    }

    try {
      const response = await apiClient.get(`/api/contacts/?query=${encodeURIComponent(query)}`);
      const contacts = response.data.results || [];
      return contacts.map(mapBackendContact);
    } catch (error) {
      console.error('Error al buscar contactos:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw new Error(apiError.message);
      }
      throw error;
    }
  },

  async createContact(contactData) {
    if (!shouldUseAPI()) {
      const mockContactId = Math.floor(Math.random() * 1000) + 100;
      const newContact = {
        id: mockContactId,
        first_name: contactData.first_name,
        last_name: contactData.last_name || '',
        cuil: contactData.cuil,
        email: contactData.email,
        phone: contactData.phone,
        phone2: contactData.phone2 || '',
        address: contactData.address || ''
      };

      const existingContacts = this.getCreatedContacts();
      const updatedContacts = [...existingContacts, newContact];
      this.saveCreatedContacts(updatedContacts);
      return { id: mockContactId };
    }

    try {
      const backendData = {
        first_name: contactData.first_name,
        last_name: contactData.last_name || '',
        cuil: contactData.cuil.replace(/\D/g, ''),
        email: contactData.email,
        phone: contactData.phone,
        phone2: contactData.phone2 || '',
        address: contactData.address || ''
      };

      const response = await apiClient.post('/api/contacts/', backendData);
      return response.data;
    } catch (error) {
      console.error('Error al crear contacto:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  validateCuitCuil(value) {
    const cleanValue = value.replace(/\D/g, '');
    return cleanValue.length === 11;
  },

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  clearCreatedContacts() {
    try {
      sessionStorage.removeItem('demo_contacts');
    } catch (error) {
      console.error('Error al limpiar contactos creados:', error);
    }
  },

  getAllContacts() {
    if (!shouldUseAPI()) {
      return [...this.getMockContacts(), ...this.getCreatedContacts()];
    }
    return [];
  },

  async getContactById(contactId) {
    if (!shouldUseAPI()) {
      const allContacts = [...this.getMockContacts(), ...this.getCreatedContacts()];
      const contact = allContacts.find((c) => c.id === contactId || String(c.id) === String(contactId));

      if (contact) {
        return contact;
      }

      throw new Error(`Contacto con ID ${contactId} no encontrado`);
    }

    try {
      const response = await apiClient.get(`/api/contacts/${contactId}/`);
      return mapBackendContact(response.data);
    } catch (error) {
      console.error('Error al obtener contacto por ID:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw new Error(apiError.message || `Error al cargar el contacto con ID ${contactId}`);
      }
      throw error;
    }
  }
};

export default contactsService;
